import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables, TablesUpdate } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { queryKeys } from "./keys";

export type Salarie = Tables<"salaries">;
export type SalarieUpdate = TablesUpdate<"salaries">;

export type SalarieWithRelations = Salarie & {
  societe: Pick<Tables<"societes">, "id" | "nom" | "slug" | "groupe_id"> | null;
  operation: Pick<
    Tables<"operations">,
    "id" | "nom" | "slug" | "annee" | "bri_type_default"
  > | null;
  site: Pick<Tables<"sites">, "id" | "nom" | "ville"> | null;
};

const SALARIE_WITH_RELATIONS_SELECT = `
  *,
  societe:societes!salaries_societe_id_fkey ( id, nom, slug, groupe_id ),
  operation:operations!salaries_operation_id_fkey ( id, nom, slug, annee, bri_type_default ),
  site:sites!salaries_site_id_fkey ( id, nom, ville )
`;

/**
 * Hook : la fiche salarié liée à l'utilisateur courant.
 * Renvoie null si l'utilisateur n'est pas (encore) rattaché à une fiche salarié.
 */
export function useMySalarie() {
  const { effectiveUserId } = useAuth();
  return useQuery({
    queryKey: effectiveUserId ? queryKeys.salarie.mine(effectiveUserId) : ["salarie", "anonymous"],
    queryFn: async (): Promise<SalarieWithRelations | null> => {
      if (!effectiveUserId) return null;
      const { data, error } = await supabase
        .from("salaries")
        .select(SALARIE_WITH_RELATIONS_SELECT)
        .eq("auth_user_id", effectiveUserId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as SalarieWithRelations | null;
    },
    enabled: Boolean(effectiveUserId),
  });
}

/**
 * Hook : fiche salarié par id (Aon interne / DRH avec scope).
 */
export function useSalarie(salarieId: string | undefined) {
  return useQuery({
    queryKey: salarieId ? queryKeys.salarie.byId(salarieId) : ["salarie", "none"],
    queryFn: async (): Promise<SalarieWithRelations | null> => {
      if (!salarieId) return null;
      const { data, error } = await supabase
        .from("salaries")
        .select(SALARIE_WITH_RELATIONS_SELECT)
        .eq("id", salarieId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as SalarieWithRelations | null;
    },
    enabled: Boolean(salarieId),
  });
}

/**
 * Hook : date de dernière connexion réelle du salarié.
 *
 * Lit `auth.users.last_sign_in_at` via la RPC `salarie_last_sign_in`
 * (SECURITY DEFINER gardée par has_access_to_salarie). Renvoie `null` si le
 * salarié n'a jamais de compte ou ne s'est jamais connecté — à distinguer du
 * simple « compte créé » (auth_user_id non nul). CDC §8b.
 */
export function useSalarieLastSignIn(salarieId: string | undefined) {
  return useQuery({
    queryKey: ["salarie", "last-sign-in", salarieId ?? "none"] as const,
    enabled: Boolean(salarieId),
    queryFn: async (): Promise<string | null> => {
      if (!salarieId) return null;
      const { data, error } = await supabase.rpc("salarie_last_sign_in", {
        p_salarie_id: salarieId,
      });
      if (error) throw error;
      return (data as string | null) ?? null;
    },
  });
}

/**
 * Mutation : édition d'une fiche salarié PAR ID.
 *
 * Contrairement à `useUpdateMySalarie` (qui cible `auth_user_id = auth.uid()`),
 * cette mutation cible explicitement `salaries.id`. Indispensable pour la prise
 * en charge du parcours d'un salarié par le staff (Admin / DRH / AON) : sinon
 * l'écriture viserait la fiche du membre staff connecté, pas le salarié édité.
 * Le RLS garantit l'accès (has_access_to_salarie). Fonctionne aussi pour le
 * salarié sur sa propre fiche (id = sa propre ligne).
 */
export function useUpdateSalarieById() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: SalarieUpdate }): Promise<Salarie> => {
      const { data, error } = await supabase
        .from("salaries")
        .update(patch)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Rafraîchit toutes les vues d'une fiche salarié (mine / byId / listes).
      void queryClient.invalidateQueries({ queryKey: queryKeys.salarie.byId(data.id) });
      void queryClient.invalidateQueries({ queryKey: ["salarie"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "salaries"] });
    },
  });
}

/**
 * Mutation : édition de sa propre fiche salarié.
 */
export function useUpdateMySalarie() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: SalarieUpdate): Promise<Salarie> => {
      if (!user) throw new Error("Non connecté");
      const { data, error } = await supabase
        .from("salaries")
        .update(patch)
        .eq("auth_user_id", user.id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (user) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.salarie.mine(user.id) });
      }
    },
  });
}
